import React from 'react';
import { RouteComponentProps } from 'react-router';
import Card from '../components/Card';
import Carousel from '../components/Carousel';
import TagsHoc from '../components/hoc/TagsHoc';
import { useData } from '../context/DataContext';
import { useFavorites } from '../context/FavoritesContext';
import defaultImage from '../images/lineup-default.png';
import { useQueryStrings } from '../context/QueryStringContext';
import Popup from '../components/Popup';
import SimilarTimesHoc from '../components/hoc/SimilarTimesHoc';
import OtherTimes from '../components/OtherTimes';
import { useApp } from '../context/AppContext';

interface VenueDetailsPagePropsBase {
  venueId: string;
}

type VenueDetailsPageProps = VenueDetailsPagePropsBase & RouteComponentProps;

export default function VenueDetailsPage(props: VenueDetailsPageProps) {
  const { todayLineup } = useData();
  const { favorites, toggleFavorite } = useFavorites();
  const { updateQs } = useQueryStrings();
  const { config } = useApp();

  const venue = todayLineup.venues.find(venue => venue.templateId === props.venueId);

  const events = venue ? todayLineup.events.filter(evt => evt.venueId === venue.templateId) : [];
  const offers = venue
    ? todayLineup.offers.filter(offer => offer.venueId === venue.templateId)
    : [];

  return (
    <Popup onClose={() => updateQs('venue', undefined)}>
      {venue && (
        <div className="border-b border-slate-300">
          <TagsHoc type="venue" item={venue} key={venue.templateId}>
            {props => (
              <Card
                title={venue.venueString}
                subtitle={venue.timeStrings}
                description={venue.description}
                image={venue.images[0]?.url || defaultImage}
                tags={props.tags}
                isFavorite={!!favorites.venues[venue.templateId]}
                onFavoriteClick={() => toggleFavorite({ type: 'venues', value: venue })}
                detailed
                callToActions={venue.callToActions}
              />
            )}
          </TagsHoc>
          <hr className="border-slate-300" />
          <div className="py-4 bg-white">
            <SimilarTimesHoc type="venues" item={venue}>
              {({ similarTimes }) => <OtherTimes similarTimes={similarTimes} />}
            </SimilarTimesHoc>
          </div>
          {events.length > 0 && config.visibleTabs.includes('lineup') && (
            <Carousel
              icon={['far', 'calendar-day']}
              title="Events"
              className="carousel-container py-4 border-t border-slate-300"
            >
              {events.map(evt => (
                <TagsHoc type="basic" item={evt} key={evt.id}>
                  {props => (
                    <Card
                      className="carousel border"
                      onClick={() => updateQs('event', evt.id)}
                      title={evt.title}
                      subtitle={[evt.venueString, evt.timeString]}
                      image={evt.images[0]?.url || defaultImage}
                      tags={props.tags}
                      isFavorite={!!favorites.events[evt.id]}
                      onFavoriteClick={() => toggleFavorite({ type: 'events', value: evt })}
                    />
                  )}
                </TagsHoc>
              ))}
            </Carousel>
          )}
          {offers.length > 0 && config.visibleTabs.includes('offers') && (
            <Carousel
              title="Offers"
              icon={['far', 'hand-holding-circle-dollar']}
              className={`carousel-container py-4 border-slate-300${
                events.length < 1 ? ' border-t' : ''
              }`}
            >
              {offers.map(offer => (
                <TagsHoc type="basic" item={offer} key={offer.id}>
                  {props => (
                    <Card
                      className="carousel border"
                      onClick={() => updateQs('offer', offer.id)}
                      title={offer.title}
                      subtitle={offer.description}
                      image={offer.images[0]?.url || defaultImage}
                      tags={props.tags}
                      isFavorite={!!favorites.offers[offer.id]}
                      onFavoriteClick={() => toggleFavorite({ type: 'offers', value: offer })}
                    />
                  )}
                </TagsHoc>
              ))}
            </Carousel>
          )}
        </div>
      )}
    </Popup>
  );
}
